import type { JSX } from "react";

import type { Series } from "highcharts";
import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { IDurationObject } from "app/services/DurationService";

import { ETypes, type IDataPoint, type IMetaData } from "../../types";

import { TooltipTitle } from "./BubbleChartTooltipTitle";
import { getAverageColumnsDefinition, getColumnsDefinition, getPeriodOverPeriodColumnsDefinition } from "./columns";
import { getAverageTooltipData, getPeriodOverPeriodTooltipData, getTooltipData } from "./dataResolvers";

const TooltipContainer = styled.div`
    padding: 8px 12px 12px 12px;
    background-color: ${colorsPalettes.carbon[0]};
    box-shadow: 0 3px 6px 0 ${rgba(colorsPalettes.carbon[200], 0.5)};
    border-radius: 5px;
`;

interface BubbleChartTooltipProps {
    isAverage: boolean;
    isPeriodOverPeriod: boolean;
    isPreviousPeriod: boolean;
    filters: IDurationObject<string>;
    metaData: IMetaData;
    series: Series;
    options: IDataPoint;
    type: ETypes;
    favicon: string;
    subtitle: string;
    entityDisplayName: string;
}

export const BubbleChartTooltip = ({
    isAverage,
    isPreviousPeriod,
    isPeriodOverPeriod,
    filters,
    metaData,
    series,
    options,
    type,
    favicon,
    subtitle,
    entityDisplayName,
}: BubbleChartTooltipProps) => {
    let tooltipContent: JSX.Element;

    const singlePeriodTooltipWidth = type === ETypes.ConversionRate ? 500 : 440;
    const periodOverPeriodTooltipWidth = type === ETypes.ConversionRate ? 560 : 510;

    if (isAverage) {
        tooltipContent = (
            <ChartTooltip
                data={getAverageTooltipData(metaData, type)}
                columnDefinition={getAverageColumnsDefinition()}
                width={300}
                title={translate("common.average")}
            />
        );
    } else if (isPeriodOverPeriod) {
        tooltipContent = (
            <ChartTooltip
                data={getPeriodOverPeriodTooltipData(series, type)}
                columnDefinition={getPeriodOverPeriodColumnsDefinition(filters, isPreviousPeriod)}
                width={periodOverPeriodTooltipWidth}
                title={
                    <TooltipTitle
                        entityDisplayName={entityDisplayName}
                        favicon={favicon}
                        subtitle={subtitle}
                    />
                }
            />
        );
    } else {
        tooltipContent = (
            <ChartTooltip
                data={getTooltipData(options, metaData, type, series)}
                columnDefinition={getColumnsDefinition()}
                width={singlePeriodTooltipWidth}
                title={
                    <TooltipTitle
                        entityDisplayName={entityDisplayName}
                        favicon={favicon}
                        subtitle={subtitle}
                    />
                }
            />
        );
    }

    return <TooltipContainer>{tooltipContent}</TooltipContainer>;
};
